export const SIGNAL_STATUS = {
  UNDEFINED: 'undefined',
  EXCELLENT: 'excellent',
  VERY_GOOD: 'very_good',
  GOOD: 'good',
  FAIR: 'fair',
  POOR: 'poor',
  VERY_POOR: 'very_poor',
};

export type SignalStatusValue = typeof SIGNAL_STATUS[keyof typeof SIGNAL_STATUS];

export const SIGNAL_STATUS_RANK: Record<string, number> = {
  [SIGNAL_STATUS.UNDEFINED]: -1,
  [SIGNAL_STATUS.EXCELLENT]: 0,
  [SIGNAL_STATUS.VERY_GOOD]: 1,
  [SIGNAL_STATUS.GOOD]: 2,
  [SIGNAL_STATUS.FAIR]: 3,
  [SIGNAL_STATUS.POOR]: 4,
  [SIGNAL_STATUS.VERY_POOR]: 5,
};

export const SIGNAL_STATUS_LABEL: Record<string, string> = {
  [SIGNAL_STATUS.EXCELLENT]: 'Excellent',
  [SIGNAL_STATUS.VERY_GOOD]: 'Very Good',
  [SIGNAL_STATUS.GOOD]: 'Good',
  [SIGNAL_STATUS.FAIR]: 'Fair',
  [SIGNAL_STATUS.POOR]: 'Poor',
  [SIGNAL_STATUS.VERY_POOR]: 'Very Poor',
  [SIGNAL_STATUS.UNDEFINED]: 'No signal data',
};

export const SIGNAL_STATUS_DOT_COLOR: Record<string, string> = {
  [SIGNAL_STATUS.EXCELLENT]: 'rgb(16, 185, 129)',
  [SIGNAL_STATUS.VERY_GOOD]: 'rgb(34, 197, 94)',
  [SIGNAL_STATUS.GOOD]: '#ffcc00',
  [SIGNAL_STATUS.FAIR]: 'rgba(255,122,15,0.88)',
  [SIGNAL_STATUS.POOR]: '#ff2a2a',
  [SIGNAL_STATUS.VERY_POOR]: '#1a1a1a',
  [SIGNAL_STATUS.UNDEFINED]: '#6c757d',
};

export const parseRouterSignalMetric = (value: unknown): number | null => {
  if (value == null || value === '') {
    return null;
  }
  const numeric = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(numeric) || numeric === 0) {
    return null;
  }
  return numeric;
};

export const isRouterSignalMetricPresent = (value: unknown): boolean => parseRouterSignalMetric(value) != null;

export const formatRouterSignalMetricValue = (value: unknown, noneLabel = 'none'): string => {
  const numeric = parseRouterSignalMetric(value);
  return numeric != null ? String(numeric) : noneLabel;
};

export class SignalStatus {
  static rssi(signal: unknown): string {
    return SignalStatus.rsrp(signal);
  }

  static sinr(signal: unknown): string {
    const numeric = parseRouterSignalMetric(signal);
    if (numeric == null) {
      return SIGNAL_STATUS.UNDEFINED;
    } else if (numeric >= 20) {
      return SIGNAL_STATUS.EXCELLENT;
    } else if (numeric >= 13) {
      return SIGNAL_STATUS.VERY_GOOD;
    } else if (numeric >= 5) {
      return SIGNAL_STATUS.GOOD;
    } else if (numeric >= 0) {
      return SIGNAL_STATUS.FAIR;
    } else if (numeric >= -5) {
      return SIGNAL_STATUS.POOR;
    } else {
      return SIGNAL_STATUS.VERY_POOR;
    }
  }

  static rsrq(signal: unknown): string {
    const numeric = parseRouterSignalMetric(signal);
    if (numeric == null) {
      return SIGNAL_STATUS.UNDEFINED;
    } else if (numeric >= -10) {
      return SIGNAL_STATUS.EXCELLENT;
    } else if (numeric >= -12) {
      return SIGNAL_STATUS.VERY_GOOD;
    } else if (numeric >= -15) {
      return SIGNAL_STATUS.GOOD;
    } else if (numeric >= -20) {
      return SIGNAL_STATUS.FAIR;
    } else {
      return SIGNAL_STATUS.VERY_POOR;
    }
  }

  static rsrp(signal: unknown): string {
    const numeric = parseRouterSignalMetric(signal);
    if (numeric == null) {
      return SIGNAL_STATUS.UNDEFINED;
    } else if (numeric >= -80) {
      return SIGNAL_STATUS.EXCELLENT;
    } else if (numeric >= -90) {
      return SIGNAL_STATUS.VERY_GOOD;
    } else if (numeric >= -100) {
      return SIGNAL_STATUS.GOOD;
    } else if (numeric >= -110) {
      return SIGNAL_STATUS.FAIR;
    } else if (numeric >= -120) {
      return SIGNAL_STATUS.POOR;
    } else {
      return SIGNAL_STATUS.VERY_POOR;
    }
  }

  static worst(qualities: Array<string | null | undefined>): string {
    let worst = SIGNAL_STATUS.UNDEFINED;
    let worstRank = SIGNAL_STATUS_RANK[SIGNAL_STATUS.UNDEFINED];

    for (const quality of qualities) {
      if (!quality || quality === SIGNAL_STATUS.UNDEFINED) {
        continue;
      }
      const rank = SIGNAL_STATUS_RANK[quality] ?? -1;
      if (rank > worstRank) {
        worst = quality;
        worstRank = rank;
      }
    }

    return worst;
  }

  static interfaceOverall(iface: {
    type?: string | null;
    rssi?: number | string | null;
    sinr?: number | string | null;
    rsrq?: number | string | null;
    rsrp?: number | string | null;
  }): string {
    if (iface.type === 'ethernet') {
      return SIGNAL_STATUS.UNDEFINED;
    }
    return SignalStatus.worst([
      SignalStatus.rsrp(iface.rsrp),
      SignalStatus.rsrq(iface.rsrq),
      SignalStatus.sinr(iface.sinr),
      SignalStatus.rssi(iface.rssi),
    ]);
  }

  static percent(percent: number | null | undefined): string {
    if (percent == null) {
      return SIGNAL_STATUS.UNDEFINED;
    }
    if (percent >= 80) {
      return SIGNAL_STATUS.EXCELLENT;
    }
    if (percent >= 65) {
      return SIGNAL_STATUS.VERY_GOOD;
    }
    if (percent >= 50) {
      return SIGNAL_STATUS.GOOD;
    }
    if (percent >= 35) {
      return SIGNAL_STATUS.FAIR;
    }
    if (percent >= 20) {
      return SIGNAL_STATUS.POOR;
    }
    return SIGNAL_STATUS.VERY_POOR;
  }

  static routerOverall(router: {
    router_interfaces?: Array<{
      type?: string | null;
      rssi?: number | string | null;
      sinr?: number | string | null;
      rsrq?: number | string | null;
      rsrp?: number | string | null;
    }> | null;
    signal_percent?: number | null;
  }): string {
    const fromInterfaces = SignalStatus.worst((router.router_interfaces || []).map(SignalStatus.interfaceOverall));
    if (fromInterfaces !== SIGNAL_STATUS.UNDEFINED) {
      return fromInterfaces;
    }
    return SignalStatus.percent(router.signal_percent);
  }

  static hasInterfaceMetrics(router: {
    router_interfaces?: Array<{
      type?: string | null;
      rssi?: number | string | null;
      sinr?: number | string | null;
      rsrq?: number | string | null;
      rsrp?: number | string | null;
    }> | null;
  }): boolean {
    return (
      SignalStatus.worst((router.router_interfaces || []).map(SignalStatus.interfaceOverall)) !==
      SIGNAL_STATUS.UNDEFINED
    );
  }

  static shouldShowSignalPercent(router: {
    router_interfaces?: Array<{
      type?: string | null;
      rssi?: number | string | null;
      sinr?: number | string | null;
      rsrq?: number | string | null;
      rsrp?: number | string | null;
    }> | null;
    signal_percent?: number | null;
  }): boolean {
    return router.signal_percent != null && !SignalStatus.hasInterfaceMetrics(router);
  }

  static label(quality: string | null | undefined): string {
    return SIGNAL_STATUS_LABEL[quality || SIGNAL_STATUS.UNDEFINED] || SIGNAL_STATUS_LABEL[SIGNAL_STATUS.UNDEFINED];
  }
}
